import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Compass, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { t } from '@/i18n';
import { useMileageApi } from '@/services/api';
import type { MileagePlace } from '@ip/shared-types';

const COLUMN_COUNT = 8;

interface MileagePlacesPanelProps {
  companyNip: string;
  vehicleId: string;
}

interface PlaceFormState {
  name: string;
  addressCity: string;
  addressStreet: string;
  addressNumber: string;
  distanceKm: string;
  defaultPurpose: string;
  visitWeight: number;
  monthlyMinVisits: string;
  monthlyMaxVisits: string;
  isRoundTrip: boolean;
}

const EMPTY_PLACE_FORM: PlaceFormState = {
  name: '',
  addressCity: '',
  addressStreet: '',
  addressNumber: '',
  distanceKm: '',
  defaultPurpose: '',
  visitWeight: 5,
  monthlyMinVisits: '1',
  monthlyMaxVisits: '3',
  isRoundTrip: true,
};

export function MileagePlacesPanel({ companyNip, vehicleId }: MileagePlacesPanelProps) {
  const [places, setPlaces] = useState<MileagePlace[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlace, setEditingPlace] = useState<MileagePlace | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<PlaceFormState>(EMPTY_PLACE_FORM);

  const {
    getPlaces,
    createMileagePlace,
    updateMileagePlace,
    deleteMileagePlace,
    computePlaceRoutes,
  } = useMileageApi();

  const setField = <K extends keyof PlaceFormState>(field: K, value: PlaceFormState[K]) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const loadPlaces = useCallback(async () => {
    if (!vehicleId) return;
    setLoading(true);
    try {
      const data = await getPlaces(companyNip, vehicleId);
      setPlaces(data);
    } catch (error) {
      console.error('Failed to load places:', error);
      toast.error(t('errors.loadingFailed'));
    } finally {
      setLoading(false);
    }
  }, [companyNip, vehicleId, getPlaces]);

  useEffect(() => {
    loadPlaces();
  }, [loadPlaces]);

  const handleAdd = () => {
    setEditingPlace(null);
    setForm(EMPTY_PLACE_FORM);
    setModalOpen(true);
  };

  const handleEdit = (place: MileagePlace) => {
    setEditingPlace(place);
    setForm({
      name: place.name,
      addressCity: place.addressCity,
      addressStreet: place.addressStreet || '',
      addressNumber: place.addressNumber || '',
      distanceKm: String(place.distanceKm),
      defaultPurpose: place.defaultPurpose,
      visitWeight: place.visitWeight,
      monthlyMinVisits: place.monthlyMinVisits != null ? String(place.monthlyMinVisits) : '',
      monthlyMaxVisits: place.monthlyMaxVisits != null ? String(place.monthlyMaxVisits) : '',
      isRoundTrip: place.isRoundTrip,
    });
    setModalOpen(true);
  };

  const validateForm = (): boolean => {
    if (!form.name.trim()) {
      toast.error(t('employees.validation.required', { label: t('mileage.places.name') }));
      return false;
    }
    if (!form.addressCity.trim()) {
      toast.error(t('employees.validation.required', { label: t('mileage.places.city') }));
      return false;
    }
    const distanceKm = Number(form.distanceKm);
    if (form.distanceKm === '' || Number.isNaN(distanceKm)) {
      toast.error(t('employees.validation.required', { label: t('mileage.routes.distanceKm') }));
      return false;
    }
    if (distanceKm < 1) {
      toast.error(t('mileage.places.distanceMin'));
      return false;
    }
    if (!form.defaultPurpose.trim()) {
      toast.error(t('employees.validation.required', { label: t('mileage.routes.defaultPurpose') }));
      return false;
    }
    const minVisits = form.monthlyMinVisits === '' ? undefined : Number(form.monthlyMinVisits);
    const maxVisits = form.monthlyMaxVisits === '' ? undefined : Number(form.monthlyMaxVisits);
    if (minVisits !== undefined && (Number.isNaN(minVisits) || minVisits < 0 || minVisits > 20)) {
      toast.error(t('mileage.places.visitsRange', { label: t('mileage.places.monthlyMinVisits') }));
      return false;
    }
    if (maxVisits !== undefined && (Number.isNaN(maxVisits) || maxVisits < 0 || maxVisits > 20)) {
      toast.error(t('mileage.places.visitsRange', { label: t('mileage.places.monthlyMaxVisits') }));
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      const base = {
        name: form.name,
        addressCity: form.addressCity,
        distanceKm: Number(form.distanceKm),
        defaultPurpose: form.defaultPurpose,
        visitWeight: form.visitWeight,
        monthlyMinVisits: form.monthlyMinVisits === '' ? undefined : Number(form.monthlyMinVisits),
        monthlyMaxVisits: form.monthlyMaxVisits === '' ? undefined : Number(form.monthlyMaxVisits),
        isRoundTrip: form.isRoundTrip,
      };
      if (editingPlace) {
        // Send raw strings on update: '' clears the nullable street/number columns
        // (old form library sent '' too); mapping to undefined would silently no-op.
        await updateMileagePlace(companyNip, vehicleId, editingPlace.id, {
          ...base,
          addressStreet: form.addressStreet,
          addressNumber: form.addressNumber,
        });
      } else {
        await createMileagePlace(companyNip, vehicleId, {
          ...base,
          addressStreet: form.addressStreet || undefined,
          addressNumber: form.addressNumber || undefined,
        });
      }
      toast.success(t('common.save') + ' OK');
      setModalOpen(false);
      loadPlaces();
    } catch (error) {
      console.error('Failed to save place:', error);
      toast.error(t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (placeId: string) => {
    try {
      await deleteMileagePlace(companyNip, vehicleId, placeId);
      toast.success(t('common.delete') + ' OK');
      loadPlaces();
    } catch (error) {
      console.error('Failed to delete place:', error);
      toast.error(t('errors.deleteFailed'));
    }
  };

  const handleComputeRoutes = async (placeId: string) => {
    try {
      const result = await computePlaceRoutes(companyNip, vehicleId, placeId);
      toast.success(t('mileage.places.routesFound', { count: result.alternativesCount }));
      loadPlaces();
    } catch (error) {
      console.error('Failed to compute routes:', error);
      toast.error(t('mileage.places.computeFailed'));
    }
  };

  const weightBadgeClass = (w: number) =>
    w >= 7
      ? 'border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-400'
      : w >= 4
        ? ''
        : 'border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-400';

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <span className="font-medium">Mileage Places</span>
        <Button variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="size-4" />
          Add Place
        </Button>
      </div>

      <div className="bg-card overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-muted-foreground w-[160px]">Name</TableHead>
              <TableHead className="text-muted-foreground w-[120px]">City</TableHead>
              <TableHead className="text-muted-foreground w-[80px] text-right">Distance</TableHead>
              <TableHead className="text-muted-foreground w-[160px]">Purpose</TableHead>
              <TableHead className="text-muted-foreground w-[70px] text-center">Weight</TableHead>
              <TableHead className="text-muted-foreground w-[80px] text-center">Visits/mo</TableHead>
              <TableHead className="text-muted-foreground w-[70px] text-center">Routes</TableHead>
              <TableHead className="w-[120px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : places.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground h-24 text-center">
                  No data
                </TableCell>
              </TableRow>
            ) : (
              places.map(place => (
                <TableRow key={place.id}>
                  <TableCell className="py-2">{place.name}</TableCell>
                  <TableCell className="py-2">{place.addressCity}</TableCell>
                  <TableCell className="py-2 text-right tabular-nums">
                    {Math.round(place.distanceKm)} km
                  </TableCell>
                  <TableCell className="max-w-[160px] truncate py-2">{place.defaultPurpose}</TableCell>
                  <TableCell className="py-2 text-center">
                    <Badge variant="outline" className={weightBadgeClass(place.visitWeight)}>
                      {place.visitWeight}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2 text-center">
                    {place.monthlyMinVisits}-{place.monthlyMaxVisits}
                  </TableCell>
                  <TableCell className="py-2 text-center">
                    <Badge
                      variant="outline"
                      className={
                        place.routeAlternatives?.length
                          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                          : ''
                      }
                    >
                      {place.routeAlternatives?.length || 0}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        title="Compute route alternatives"
                        onClick={() => handleComputeRoutes(place.id)}
                      >
                        <Compass className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        aria-label={t('mileage.places.editAria')}
                        onClick={() => handleEdit(place)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive size-8"
                            aria-label={t('mileage.places.deleteAria')}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete place?</AlertDialogTitle>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-white hover:bg-destructive/90"
                              onClick={() => handleDelete(place.id)}
                            >
                              {t('common.delete')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingPlace ? 'Edit Place' : 'Add Place'}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="place-name">Name</Label>
              <Input
                id="place-name"
                placeholder="TME Lodz, Hurtownia ABC"
                value={form.name}
                onChange={e => setField('name', e.target.value)}
              />
            </div>
            <div className="flex gap-4">
              <div className="flex flex-1 flex-col gap-1.5">
                <Label htmlFor="place-city">City</Label>
                <Input
                  id="place-city"
                  placeholder="Łódź"
                  value={form.addressCity}
                  onChange={e => setField('addressCity', e.target.value)}
                />
              </div>
              <div className="flex flex-1 flex-col gap-1.5">
                <Label htmlFor="place-street">Street</Label>
                <Input
                  id="place-street"
                  placeholder="ul. Rzgowska"
                  value={form.addressStreet}
                  onChange={e => setField('addressStreet', e.target.value)}
                />
              </div>
              <div className="flex w-[70px] flex-col gap-1.5">
                <Label htmlFor="place-number">Number</Label>
                <Input
                  id="place-number"
                  placeholder="30"
                  value={form.addressNumber}
                  onChange={e => setField('addressNumber', e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-end gap-4">
              <div className="flex w-[110px] flex-col gap-1.5">
                <Label htmlFor="place-distance">Distance (km)</Label>
                <Input
                  id="place-distance"
                  type="number"
                  min={1}
                  step={1}
                  value={form.distanceKm}
                  onChange={e => setField('distanceKm', e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="place-round-trip">Round trip</Label>
                <div className="flex h-9 items-center">
                  <Switch
                    id="place-round-trip"
                    checked={form.isRoundTrip}
                    onCheckedChange={checked => setField('isRoundTrip', checked)}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="place-purpose">Default Purpose</Label>
              <Input
                id="place-purpose"
                placeholder="Odbiór towaru"
                value={form.defaultPurpose}
                onChange={e => setField('defaultPurpose', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="place-weight">Visit Weight (1=rare, 10=frequent)</Label>
              <div className="flex items-center gap-3">
                <input
                  id="place-weight"
                  type="range"
                  min={1}
                  max={10}
                  step={1}
                  value={form.visitWeight}
                  onChange={e => setField('visitWeight', Number(e.target.value))}
                  className="accent-primary w-full"
                />
                <Badge variant="outline" className="w-8 justify-center tabular-nums">
                  {form.visitWeight}
                </Badge>
              </div>
              <div className="text-muted-foreground flex justify-between px-0.5 text-xs">
                <span>1</span>
                <span>5</span>
                <span>10</span>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex w-[130px] flex-col gap-1.5">
                <Label htmlFor="place-min-visits">Min visits/month</Label>
                <Input
                  id="place-min-visits"
                  type="number"
                  min={0}
                  max={20}
                  value={form.monthlyMinVisits}
                  onChange={e => setField('monthlyMinVisits', e.target.value)}
                />
              </div>
              <div className="flex w-[130px] flex-col gap-1.5">
                <Label htmlFor="place-max-visits">Max visits/month</Label>
                <Input
                  id="place-max-visits"
                  type="number"
                  min={0}
                  max={20}
                  value={form.monthlyMaxVisits}
                  onChange={e => setField('monthlyMaxVisits', e.target.value)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={saving} onClick={handleSave}>
              {saving && <Loader2 className="size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
